import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Calculator, TrendingUp, Heart, Users, Baby, Shield, FileText, Euro, ChevronDown, ChevronUp, Trophy } from 'lucide-react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import { getFullUrl } from '../utils/url';

const ComparateurStatuts = () => {
  const [formData, setFormData] = useState({
    chiffreAffaires: 50000,
    charges: 10000,
    typeActivite: 'commerciale'
  });

  const [expandedSections, setExpandedSections] = useState({
    revenus: true,
    retraite: false,
    sante: false,
    maternite: false,
    invalidite: false,
    juridique: false
  });

  const statuts = [
    {
      id: 'ae',
      nom: 'Auto-Entrepreneur',
      sigle: 'AE',
      color: 'bg-blue-500',
      description: 'Régime micro-social simplifié'
    },
    {
      id: 'ei',
      nom: 'Entreprise Individuelle',
      sigle: 'EI',
      color: 'bg-green-500',
      description: 'Régime des indépendants'
    },
    {
      id: 'sasu',
      nom: 'SASU',
      sigle: 'SASU',
      color: 'bg-purple-500',
      description: 'Assimilé salarié'
    }
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const calculerComparaison = () => {
    const ca = parseFloat(formData.chiffreAffaires) || 0;
    const charges = parseFloat(formData.charges) || 0;
    const benefice = ca - charges;

    // Auto-Entrepreneur
    const tauxCotisationsAE = formData.typeActivite === 'commerciale' ? 0.126 : 0.22;
    const abattementAE = formData.typeActivite === 'commerciale' ? 0.71 : 0.50;
    const cotisationsAE = ca * tauxCotisationsAE;
    const revenuImposableAE = ca * (1 - abattementAE);
    const impotAE = revenuImposableAE * 0.10;
    const revenuNetAE = ca - cotisationsAE - impotAE;

    // EI (Entreprise Individuelle)
    const cotisationsEI = benefice * 0.45;
    const revenuImposableEI = benefice - cotisationsEI;
    const impotEI = revenuImposableEI * 0.15;
    const revenuNetEI = revenuImposableEI - impotEI;

    // SASU - Selon URSSAF: 44% de cotisations sociales sur le bénéfice
    // Le bénéfice est redistribué en salaire, et les cotisations représentent ~44% du bénéfice
    const cotisationsSASU = benefice * 0.44;
    const salaireBrutSASU = benefice - cotisationsSASU;
    // Charges salariales (~22% du brut)
    const chargesSalarialesSASU = salaireBrutSASU * 0.22;
    const salaireNetSASU = salaireBrutSASU - chargesSalarialesSASU;
    const impotSASU = salaireNetSASU * 0.02; // Taux d'impôt très faible car salaire bas
    const revenuNetSASU = salaireNetSASU - impotSASU;

    // Retraite (approximations mensuelles)
    const retraiteBaseAE = Math.min(revenuImposableAE * 0.50 / 12, 580);
    const retraiteBaseEI = Math.min(revenuImposableEI * 0.50 / 12, 1084);
    const retraiteBaseSASU = Math.min(salaireNetSASU * 0.50 / 12, 1074);

    const retraiteCompAE = 50;
    const retraiteCompEI = 95;
    const retraiteCompSASU = 95;

    return {
      ae: {
        revenuNetMensuel: Math.round(revenuNetAE / 12),
        revenuNetAnnuel: Math.round(revenuNetAE),
        cotisations: Math.round(cotisationsAE),
        tauxCotisations: ((cotisationsAE / ca) * 100).toFixed(1),
        retraiteBase: Math.round(retraiteBaseAE),
        retraiteComplementaire: retraiteCompAE,
        arretMaladie: 19,
        congeMaternite: 65,
        allocationMaternite: 3925,
        invaliditePartielle: 487,
        invaliditeTotale: 686,
        capitalDeces: 9420,
        pensionReversion: 313,
        coutCreation: 0
      },
      ei: {
        revenuNetMensuel: Math.round(revenuNetEI / 12),
        revenuNetAnnuel: Math.round(revenuNetEI),
        cotisations: Math.round(cotisationsEI),
        tauxCotisations: ((cotisationsEI / benefice) * 100).toFixed(1),
        retraiteBase: Math.round(retraiteBaseEI),
        retraiteComplementaire: retraiteCompEI,
        arretMaladie: 36,
        congeMaternite: 65,
        allocationMaternite: 3925,
        invaliditePartielle: 650,
        invaliditeTotale: 1084,
        capitalDeces: 9420,
        pensionReversion: 585,
        coutCreation: 0
      },
      sasu: {
        revenuNetMensuel: Math.round(revenuNetSASU / 12),
        revenuNetAnnuel: Math.round(revenuNetSASU),
        cotisations: Math.round(cotisationsSASU + chargesSalarialesSASU),
        tauxCotisations: (((cotisationsSASU + chargesSalarialesSASU) / benefice) * 100).toFixed(1),
        retraiteBase: Math.round(retraiteBaseSASU),
        retraiteComplementaire: retraiteCompSASU,
        arretMaladie: 35,
        congeMaternite: 56,
        allocationMaternite: 0,
        invaliditePartielle: 645,
        invaliditeTotale: 1074,
        capitalDeces: 3681,
        pensionReversion: 580,
        coutCreation: 180
      }
    };
  };

  const resultats = calculerComparaison();

  const formatMontant = (montant) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(montant);
  };

  const getBestValue = (values, isLower = false) => {
    if (isLower) {
      return Math.min(...values);
    }
    return Math.max(...values);
  };

  const isBest = (value, values, isLower = false) => {
    const best = getBestValue(values, isLower);
    return value === best;
  };

  const ComparisonCard = ({ title, icon: Icon, data, unit = '€', isLower = false, section }) => {
    const values = Object.values(data);
    const isExpanded = expandedSections[section];

    return (
      <Card className="bg-white shadow-md hover:shadow-lg transition-shadow">
        <CardContent className="p-6">
          <button 
            onClick={() => toggleSection(section)}
            className="w-full flex items-center justify-between mb-4 hover:opacity-80 transition-opacity"
          >
            <div className="flex items-center space-x-3">
              <Icon className="w-6 h-6 text-primary-blue" />
              <h3 className="text-xl font-bold text-text-dark">{title}</h3>
            </div>
            {isExpanded ? 
              <ChevronUp className="w-5 h-5 text-primary-blue" /> : 
              <ChevronDown className="w-5 h-5 text-primary-blue" />
            }
          </button>

          {isExpanded && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {statuts.map((statut, index) => {
                const value = Object.values(data)[index];
                const best = isBest(value, values, isLower);
                
                return (
                  <div 
                    key={statut.id}
                    className={`p-4 rounded-lg border-2 transition-all ${
                      best 
                        ? 'border-green-500 bg-green-50' 
                        : 'border-gray-200 bg-gray-50'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className={`px-3 py-1 rounded-full text-white text-sm font-medium ${statut.color}`}>
                        {statut.sigle}
                      </span>
                      {best && <Trophy className="w-5 h-5 text-green-600" />}
                    </div>
                    <p className="text-2xl font-bold text-text-dark">
                      {typeof value === 'number' ? formatMontant(value) : value}
                      {unit && unit !== '€' && <span className="text-sm ml-1">{unit}</span>}
                    </p>
                    <p className="text-xs text-text-gray mt-1">{statut.nom}</p>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="min-h-screen">
      <SEO 
        title="Comparateur de Statuts Juridiques - MMD CONSEIL | AE, EI, SASU"
        description="Comparez Auto-entrepreneur, Entreprise Individuelle et SASU : revenus, retraite, protection sociale. Trouvez le meilleur statut pour votre activité - Expert-comptable Lyon"
        keywords="comparateur statut juridique, auto-entrepreneur vs SASU, EI ou SASU, choix statut entreprise, MMD CONSEIL Lyon"
        url={getFullUrl("/comparateur-statuts")}
      />
      
      {/* Hero Section */}
      <section className="hero-gradient pt-20 pb-16 md:pt-28 md:pb-24">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="fade-in-up">
              <Badge className="mb-6 bg-white text-primary-blue px-4 py-2 text-sm font-medium">
                Comparaison Gratuite
              </Badge>
              <h1 className="text-4xl md:text-6xl font-bold text-white mb-6 leading-tight">
                Comparateur de Statuts Juridiques
              </h1>
              <p className="text-xl md:text-2xl text-white mb-8 leading-relaxed">
                Auto-entrepreneur, EI ou SASU ? Comparez revenus, retraite et protection sociale
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-8 bg-blue-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <Card className="bg-white border-l-4 border-primary-blue">
              <CardContent className="p-6">
                <h2 className="text-xl font-bold text-primary-blue mb-3">
                  Trouvez le statut le plus adapté pour votre activité
                </h2>
                <p className="text-text-gray">
                  Le choix du statut juridique détermine votre régime social et impacte directement 
                  vos revenus, votre protection sociale et vos obligations comptables. 
                  Comparez les 3 principaux statuts pour faire le bon choix.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Formulaire */}
      <section className="section-spacing bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto mb-12">
            <Card className="bg-white shadow-lg">
              <CardContent className="p-8">
                <div className="flex items-center space-x-3 mb-6">
                  <Calculator className="w-6 h-6 text-primary-blue" />
                  <h2 className="text-2xl font-bold text-text-dark">Paramètres de comparaison</h2>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Chiffre d'affaires */}
                  <div>
                    <label className="block text-text-dark font-medium mb-2">
                      Chiffre d'affaires annuel
                    </label>
                    <div className="relative">
                      <Euro className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                      <Input
                        type="number"
                        min="0"
                        step="1000"
                        value={formData.chiffreAffaires}
                        onChange={(e) => handleInputChange('chiffreAffaires', e.target.value)}
                        className="pl-10"
                      />
                    </div>
                    <p className="text-sm text-text-gray mt-1">Recettes brutes (hors taxes)</p>
                  </div>

                  {/* Charges */}
                  <div>
                    <label className="block text-text-dark font-medium mb-2">
                      Charges annuelles
                    </label>
                    <div className="relative">
                      <Euro className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                      <Input
                        type="number"
                        min="0"
                        step="1000"
                        value={formData.charges}
                        onChange={(e) => handleInputChange('charges', e.target.value)}
                        className="pl-10"
                      />
                    </div>
                    <p className="text-sm text-text-gray mt-1">Hors rémunération dirigeant</p>
                  </div>

                  {/* Type d'activité */}
                  <div className="md:col-span-2">
                    <label className="block text-text-dark font-medium mb-3">
                      Type d'activité
                    </label>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      {[
                        { value: 'commerciale', label: 'Commerciale' },
                        { value: 'service', label: 'Prestation de services' },
                        { value: 'liberale', label: 'Libérale' }
                      ].map(type => (
                        <button
                          key={type.value}
                          onClick={() => handleInputChange('typeActivite', type.value)}
                          className={`p-3 rounded-lg border-2 transition-all text-center ${
                            formData.typeActivite === type.value
                              ? 'border-primary-blue bg-light-blue text-primary-blue font-medium'
                              : 'border-gray-200 hover:border-accent-blue'
                          }`}
                        >
                          {type.label}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Comparaison des résultats */}
          <div className="max-w-6xl mx-auto space-y-6">
            <h2 className="text-3xl font-bold text-text-dark mb-8 text-center">
              Comparaison des 3 statuts
            </h2>

            {/* Revenus */}
            <ComparisonCard
              title="Vos revenus nets"
              icon={TrendingUp}
              section="revenus"
              data={{
                ae: resultats.ae.revenuNetMensuel,
                ei: resultats.ei.revenuNetMensuel,
                sasu: resultats.sasu.revenuNetMensuel
              }}
              unit="/mois"
            />

            {/* Retraite */}
            <ComparisonCard
              title="Retraite de base"
              icon={Users}
              section="retraite"
              data={{
                ae: resultats.ae.retraiteBase,
                ei: resultats.ei.retraiteBase,
                sasu: resultats.sasu.retraiteBase
              }}
              unit="/mois"
            />

            {/* Santé - Arrêt maladie */}
            <ComparisonCard
              title="Arrêt maladie"
              icon={Heart}
              section="sante"
              data={{
                ae: resultats.ae.arretMaladie,
                ei: resultats.ei.arretMaladie,
                sasu: resultats.sasu.arretMaladie
              }}
              unit="/jour"
            />

            {/* Maternité */}
            <ComparisonCard
              title="Congé maternité/paternité"
              icon={Baby}
              section="maternite"
              data={{
                ae: resultats.ae.congeMaternite,
                ei: resultats.ei.congeMaternite,
                sasu: resultats.sasu.congeMaternite
              }}
              unit="/jour"
            />

            {/* Invalidité */}
            <ComparisonCard
              title="Pension invalidité totale"
              icon={Shield}
              section="invalidite"
              data={{
                ae: resultats.ae.invaliditeTotale,
                ei: resultats.ei.invaliditeTotale,
                sasu: resultats.sasu.invaliditeTotale
              }}
              unit="/mois"
            />

            {/* Juridique */}
            <ComparisonCard
              title="Coût de création"
              icon={FileText}
              section="juridique"
              data={{
                ae: resultats.ae.coutCreation,
                ei: resultats.ei.coutCreation,
                sasu: resultats.sasu.coutCreation
              }}
              unit=" HT"
              isLower={true}
            />
          </div>

          {/* Tableau récapitulatif */}
          <div className="max-w-6xl mx-auto mt-12">
            <Card className="bg-white shadow-lg">
              <CardContent className="p-8">
                <h3 className="text-2xl font-bold text-text-dark mb-6">Récapitulatif complet</h3>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b-2 border-gray-200">
                        <th className="text-left p-3 text-text-dark font-semibold">Critère</th>
                        <th className="text-center p-3">
                          <span className="px-3 py-1 rounded-full bg-blue-500 text-white text-sm">AE</span>
                        </th>
                        <th className="text-center p-3">
                          <span className="px-3 py-1 rounded-full bg-green-500 text-white text-sm">EI</span>
                        </th>
                        <th className="text-center p-3">
                          <span className="px-3 py-1 rounded-full bg-purple-500 text-white text-sm">SASU</span>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="border-b border-gray-100">
                        <td className="p-3 font-medium">Revenu net annuel</td>
                        <td className="p-3 text-center">{formatMontant(resultats.ae.revenuNetAnnuel)}</td>
                        <td className="p-3 text-center">{formatMontant(resultats.ei.revenuNetAnnuel)}</td>
                        <td className="p-3 text-center">{formatMontant(resultats.sasu.revenuNetAnnuel)}</td>
                      </tr>
                      <tr className="border-b border-gray-100">
                        <td className="p-3 font-medium">Taux cotisations</td>
                        <td className="p-3 text-center">{resultats.ae.tauxCotisations}%</td>
                        <td className="p-3 text-center">{resultats.ei.tauxCotisations}%</td>
                        <td className="p-3 text-center">{resultats.sasu.tauxCotisations}%</td>
                      </tr>
                      <tr className="border-b border-gray-100">
                        <td className="p-3 font-medium">Capital décès</td>
                        <td className="p-3 text-center">{formatMontant(resultats.ae.capitalDeces)}</td>
                        <td className="p-3 text-center">{formatMontant(resultats.ei.capitalDeces)}</td>
                        <td className="p-3 text-center">{formatMontant(resultats.sasu.capitalDeces)}</td>
                      </tr>
                      <tr className="border-b border-gray-100">
                        <td className="p-3 font-medium">Pension réversion</td>
                        <td className="p-3 text-center">{formatMontant(resultats.ae.pensionReversion)}/mois</td>
                        <td className="p-3 text-center">{formatMontant(resultats.ei.pensionReversion)}/mois</td>
                        <td className="p-3 text-center">{formatMontant(resultats.sasu.pensionReversion)}/mois</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-spacing bg-gradient-to-br from-primary-blue to-accent-blue text-white">
        <div className="container mx-auto px-4 text-center">
          <Calculator className="w-16 h-16 mx-auto mb-6 opacity-90" />
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Besoin d'aide pour choisir votre statut ?
          </h2>
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
            Nos experts-comptables vous accompagnent dans le choix du statut juridique 
            le plus adapté à votre situation et à vos objectifs.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button asChild size="lg" className="bg-white text-primary-blue hover:bg-blue-50 px-8 py-4 text-lg">
              <Link to="/contact">
                Demander un conseil personnalisé
              </Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white/10 px-8 py-4 text-lg">
              <Link to="/services">
                Nos services d'accompagnement
              </Link>
            </Button>
          </div>
        </div>
      </section>

    </div>
  );
};

export default ComparateurStatuts;
